import * as ExcelJS from 'exceljs';

import { buildExportHeaderRows } from './header-rows';
import { type ExportHeaderInfo, type PortAssignmentExportData } from './types';

const TABLE_HEADERS = [
  'Name',
  'Target',
  'Target Port',
  'Source',
  'Source Port',
  'VLAN IDs',
  'PoE',
  'DHCP',
];

export const buildPortAssignmentsExcelBlob = async (
  portAssignmentsData: PortAssignmentExportData[],
  filename: string,
  headerInfo?: ExportHeaderInfo,
): Promise<{ blob: Blob; filename: string }> => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Port Assignments');

  let dataStartRow = 1;
  if (headerInfo) {
    const headerRows = buildExportHeaderRows(headerInfo, TABLE_HEADERS.length);
    headerRows.forEach((row) => sheet.addRow(row));
    const lastHeaderRow = headerRows.length;
    for (let r = 1; r <= lastHeaderRow; r += 1) {
      const row = sheet.getRow(r);
      row.eachCell((cell) => {
        cell.alignment = {
          wrapText: true,
          vertical: 'top',
          horizontal: 'left',
        };
      });
      row.getCell(1).font = { bold: true };
    }
    if (lastHeaderRow > 1) {
      sheet.mergeCells(1, 1, 1, TABLE_HEADERS.length);
    }
    sheet.getRow(1).getCell(1).font = { bold: true, size: 12 };
    dataStartRow = headerRows.length + 1;
  }

  sheet.addRow(TABLE_HEADERS);

  sheet.getRow(dataStartRow).eachCell((cell) => {
    cell.font = { bold: true };
    cell.alignment = {
      horizontal: 'center',
      vertical: 'middle',
      wrapText: true,
    };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFDCE6F1' },
    };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  portAssignmentsData.forEach((item) => {
    const rowValues = [
      item.Name,
      item.Target,
      item['Target Port'],
      item.Source,
      item['Source Port'],
      item.vlanIdsFormatted,
      item.poe,
      item.dhcp,
    ];

    const row = sheet.addRow(rowValues);
    row.eachCell((cell, rowIndex) => {
      cell.alignment = {
        vertical: 'middle',
        horizontal: rowIndex === 0 ? 'left' : 'center',
        wrapText: true,
      };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });
  });

  const MAX_HEADER_COL_WIDTH = 70;
  sheet.columns?.forEach((col, idx) => {
    let maxLength = 0;
    col.eachCell?.({ includeEmpty: true }, (cell: any) => {
      const cellValue = cell?.value ? cell.value.toString() : '';
      maxLength = Math.max(maxLength, cellValue.length);
    });
    const w = maxLength + 2;
    col.width = idx < 2 ? Math.min(w, MAX_HEADER_COL_WIDTH) : w;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  return { blob, filename: `${filename}.xlsx` };
};

export const exportPortAssignmentsToExcel = async (
  portAssignmentsData: PortAssignmentExportData[],
  filename: string,
  headerInfo?: ExportHeaderInfo,
) => {
  const { blob, filename: fname } = await buildPortAssignmentsExcelBlob(
    portAssignmentsData,
    filename,
    headerInfo,
  );
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fname;
  link.click();
};
